import {
  BanknoteIcon,
  BriefcaseBusinessIcon,
  CheckIcon,
  GraduationCapIcon,
  HeartHandshakeIcon,
  LayoutGridIcon,
} from "lucide-react";

import { useProductLabels } from "@/api/hooks/tenant";
import { useSession } from "@/api/hooks/auth";
import type { SuiteApp } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ICONS: Record<SuiteApp["product"], typeof GraduationCapIcon> = {
  hire: BriefcaseBusinessIcon,
  learn: GraduationCapIcon,
  manage: BanknoteIcon,
  pulse: HeartHandshakeIcon,
};

const BLURBS: Record<SuiteApp["product"], string> = {
  hire: "Recruiting and appraisals",
  learn: "Courses and assessments",
  manage: "Payroll",
  pulse: "HR and self-service",
};

/** The organization's apps (from Hume ID), named without "Hume" on its hosts; hidden when it has only this one, or none. */
export function AppSwitcher() {
  const apps = useSession().data?.suite ?? [];
  const labels = useProductLabels();
  if (apps.length < 2) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Switch app">
          <LayoutGridIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{labels.apps}</DropdownMenuLabel>
        {apps.map((app) => {
          const Icon = ICONS[app.product];
          const current = app.product === "learn";
          return (
            <DropdownMenuItem key={app.product} asChild className="gap-3 py-2">
              <a href={app.url} aria-current={current ? "page" : undefined}>
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <Icon className="size-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground">{labels.product(app.product)}</span>
                  <span className="block truncate text-xs text-muted-foreground">{BLURBS[app.product]}</span>
                </span>
                {current && <CheckIcon className="size-4 text-brand" aria-label="This app" />}
              </a>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
